import { useEffect, useMemo, useRef, useState } from 'react'
import { startToolOperation, trackToolRejection, type ToolOperation } from '@saas-pages/shared/public/runtime'
import { Button, Card, CardContent } from '@saas-pages/shared/ui'
import { recorderAnalytics } from '@/features/recorder/model/analytics'
import { ActiveTimeTracker, classifyMediaError, extensionForMime, resolveRecorderMime, selectSupportedMimeType, stopStream, type RecorderError } from '@/features/recorder/model/media'
import { RecorderStatus } from '@/features/recorder/ui/RecorderStatus'
import { useLocale, useT } from '@/shared/i18n'

type Status = 'idle' | 'requesting' | 'ready' | 'recording' | 'paused' | 'finished' | 'error'
const MAX_SECONDS = 30 * 60
const MAX_DURATION_MS = MAX_SECONDS * 1000

export function VoiceRecorder() {
  const t = useT()
  const { locale } = useLocale()
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<RecorderError | null>(null)
  const [seconds, setSeconds] = useState(0)
  const [url, setUrl] = useState<string | null>(null)
  const [resultMime, setResultMime] = useState<string | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const timerRef = useRef<number | null>(null)
  const deadlineRef = useRef<number | null>(null)
  const chunkTypesRef = useRef<string[]>([])
  const operationRef = useRef<ToolOperation | null>(null)
  const activeTimeRef = useRef(new ActiveTimeTracker())
  const chunksRef = useRef<Blob[]>([])
  const failedRef = useRef(false)
  const mountedRef = useRef(true)
  const generationRef = useRef(0)
  const resultUrlRef = useRef<string | null>(null)
  const mime = useMemo(() => typeof MediaRecorder === 'undefined' ? undefined : selectSupportedMimeType('audio', MediaRecorder.isTypeSupported.bind(MediaRecorder)), [status])
  const analytics = recorderAnalytics('/diktofon-online', 'voice-recorder', locale)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      generationRef.current += 1
      clearTimers()
      const recorder = recorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        failedRef.current = true
        recorder.ondataavailable = null; recorder.onerror = null; recorder.onstop = null
        operationRef.current?.error('cancelled'); operationRef.current = null
        try { recorder.stop() } catch { /* already stopping */ }
      }
      stopStream(streamRef.current)
      if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    }
  }, [])

  async function prepare() {
    const generation = ++generationRef.current
    stopStream(streamRef.current); streamRef.current = null
    revokeResult()
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('unsupported'); setStatus('error'); trackToolRejection(analytics, 'unsupported_input'); return
    }
    setError(null); setStatus('requesting')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!mountedRef.current || generation !== generationRef.current) { stopStream(stream); return }
      streamRef.current = stream
      setStatus('ready')
    } catch (cause) {
      if (!mountedRef.current || generation !== generationRef.current) return
      setError(classifyMediaError(cause)); setStatus('error'); trackToolRejection(analytics, 'unsupported_input')
    }
  }

  function start() {
    const stream = streamRef.current
    if (!stream) return
    if (url) { URL.revokeObjectURL(url); setUrl(null) }
    const options = mime ? { mimeType: mime } : undefined
    try {
      const recorder = new MediaRecorder(stream, options)
      chunkTypesRef.current = []
      failedRef.current = false
      chunksRef.current = []
      operationRef.current = startToolOperation(analytics)
      activeTimeRef.current.start(performance.now())
      recorder.ondataavailable = (event) => { if (event.data.size > 0) { chunksRef.current.push(event.data); chunkTypesRef.current.push(event.data.type) } }
      recorder.onerror = () => {
        if (failedRef.current) return
        failedRef.current = true
        clearTimers()
        operationRef.current?.error('processing_failed'); operationRef.current = null
        stopStream(streamRef.current); streamRef.current = null
        setError('genericError'); setStatus('error')
      }
      recorder.onstop = () => {
        clearTimers()
        if (failedRef.current || !mountedRef.current) return
        const actualMime = resolveRecorderMime('audio', chunkTypesRef.current, recorder.mimeType, mime)
        if (!actualMime) {
          operationRef.current?.error('unsupported_output'); operationRef.current = null
          stopStream(streamRef.current); streamRef.current = null
          setError('genericError'); setStatus('error'); return
        }
        const blob = new Blob(chunksRef.current, { type: actualMime })
        if (blob.size === 0) {
          operationRef.current?.error('processing_failed'); operationRef.current = null
          stopStream(streamRef.current); streamRef.current = null
          setError('genericError'); setStatus('error'); return
        }
        const nextUrl = URL.createObjectURL(blob)
        resultUrlRef.current = nextUrl
        setResultMime(actualMime)
        setUrl(nextUrl)
        setStatus('finished')
        operationRef.current?.success(activeTimeRef.current.elapsed(performance.now()))
        operationRef.current = null
        stopStream(streamRef.current); streamRef.current = null
      }
      recorderRef.current = recorder
      recorder.start(250)
      setSeconds(0); setStatus('recording')
      timerRef.current = window.setInterval(() => {
        setSeconds(Math.min(MAX_SECONDS, Math.floor(activeTimeRef.current.elapsed(performance.now()) / 1000)))
      }, 250)
      scheduleAutoStop()
    } catch {
      const operation = operationRef.current
      if (operation) operation.error('processing_failed')
      else trackToolRejection(analytics, 'unsupported_output')
      operationRef.current = null
      stopStream(streamRef.current); streamRef.current = null
      setError('genericError'); setStatus('error')
    }
  }

  function stop() {
    clearTimers()
    activeTimeRef.current.pause(performance.now())
    setSeconds(Math.min(MAX_SECONDS, Math.floor(activeTimeRef.current.elapsed(performance.now()) / 1000)))
    if (recorderRef.current?.state !== 'inactive') recorderRef.current?.stop()
  }

  function togglePause() {
    const recorder = recorderRef.current
    if (!recorder) return
    if (recorder.state === 'recording') {
      recorder.pause(); activeTimeRef.current.pause(performance.now())
      if (deadlineRef.current !== null) { window.clearTimeout(deadlineRef.current); deadlineRef.current = null }
      setSeconds(Math.floor(activeTimeRef.current.elapsed(performance.now()) / 1000)); setStatus('paused')
    } else if (recorder.state === 'paused') {
      recorder.resume(); activeTimeRef.current.resume(performance.now()); scheduleAutoStop(); setStatus('recording')
    }
  }

  function scheduleAutoStop() {
    if (deadlineRef.current !== null) window.clearTimeout(deadlineRef.current)
    const remaining = activeTimeRef.current.remaining(MAX_DURATION_MS, performance.now())
    deadlineRef.current = window.setTimeout(stop, remaining)
  }

  function clearTimers() {
    if (timerRef.current !== null) window.clearInterval(timerRef.current)
    if (deadlineRef.current !== null) window.clearTimeout(deadlineRef.current)
    timerRef.current = null; deadlineRef.current = null
  }

  function reset() {
    generationRef.current += 1
    revokeResult()
    setResultMime(null); setSeconds(0); setError(null); setStatus('idle')
  }

  function revokeResult() {
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    resultUrlRef.current = null
    setUrl(null)
  }

  return (
    <Card className="overflow-hidden border-rose-200/80 bg-card/95 shadow-lg dark:border-rose-900/60">
      <CardContent className="space-y-4 p-4 sm:p-6">
        <RecorderStatus status={status} seconds={seconds} errorText={error ? t.voice.errors[error] : null} mime={resultMime ?? mime} />
        {url && <audio className="w-full" controls src={url} aria-label={t.common.preview} />}
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          {(status === 'idle' || status === 'requesting' || status === 'error') && <Button className="min-h-11" disabled={status === 'requesting'} onClick={prepare}>{status === 'error' ? t.common.retry : t.voice.allow}</Button>}
          {status === 'ready' && <Button className="min-h-11" onClick={start}>{t.common.start}</Button>}
          {(status === 'recording' || status === 'paused') && <Button variant="outline" onClick={togglePause}>{status === 'paused' ? t.common.resume : t.common.pause}</Button>}
          {(status === 'recording' || status === 'paused') && <Button onClick={stop}>{t.common.stop}</Button>}
          {status === 'finished' && url && <Button asChild><a href={url} download={`mediatik-voice.${extensionForMime(resultMime ?? 'audio/webm')}`}>{t.common.download}</a></Button>}
          {status === 'finished' && <Button variant="outline" onClick={reset}>{t.common.reset}</Button>}
        </div>
        <p className="text-sm text-muted-foreground">{t.voice.permission} {t.voice.limit}. {t.common.noUpload}.</p>
      </CardContent>
    </Card>
  )
}
